'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var Vec2 = /** @class */ (function () {
    /**
     * Initialize a new vector.
     *
     * @param x
     * @param y
     */
    function Vec2(x, y) {
        if (x === void 0) { x = 0; }
        if (y === void 0) { y = 0; }
        this.x = x;
        this.y = y;
    }
    /**
     * Create a normalized vector with a random direction.
     *
     * @param angle
     * @param length
     * @returns new vector
     */
    Vec2.random = function () {
        var x = Math.random() * (Math.round(Math.random()) ? -1 : 1);
        var y = Math.random() * (Math.round(Math.random()) ? -1 : 1);
        return new Vec2(x, y).normalize();
    };
    /**
     * Create a vector using an angle in radians as well as an optional length.
     * Angle of 0 results in an right-pointing vector.
     *
     * @param angle - in radians
     * @param length
     * @returns new vector
     */
    Vec2.fromAngle = function (angle, length) {
        if (length === void 0) { length = 1; }
        var x = Math.cos(angle) * length;
        var y = Math.sin(angle) * length;
        return new Vec2(x, y);
    };
    Object.defineProperty(Vec2.prototype, "rawPosition", {
        /**
         * Get coordinates as [x, y] array.
         */
        get: function () {
            return [this.x, this.y];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Vec2.prototype, "lengthSquared", {
        /**
         * Get squared length of vector.
         */
        get: function () {
            return this.x * this.x + this.y * this.y;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Vec2.prototype, "length", {
        /**
         * Get absolute length of vector.
         */
        get: function () {
            return Math.sqrt(this.lengthSquared);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Performs vector or scalar addition.
     *
     * @param vec
     * @returns this
     */
    Vec2.prototype.add = function (summand) {
        this.x += typeof summand === 'number' ? summand : summand.x;
        this.y += typeof summand === 'number' ? summand : summand.y;
        return this;
    };
    /**
     * Performs vector or scalar subtraction.
     *
     * @param vec
     * @returns this
     */
    Vec2.prototype.subtract = function (subtrahend) {
        this.x -= typeof subtrahend === 'number' ? subtrahend : subtrahend.x;
        this.y -= typeof subtrahend === 'number' ? subtrahend : subtrahend.y;
        return this;
    };
    /**
     * Performs vector or skalar multiplication.
     *
     * @param n
     * @returns this
     */
    Vec2.prototype.multiply = function (factor) {
        this.x *= typeof factor === 'number' ? factor : factor.x;
        this.y *= typeof factor === 'number' ? factor : factor.y;
        return this;
    };
    /**
     * Performs vector or skalar division.
     *
     * @param n
     * @returns this
     */
    Vec2.prototype.divide = function (divisor) {
        this.x /= typeof divisor === 'number' ? divisor : divisor.x;
        this.y /= typeof divisor === 'number' ? divisor : divisor.y;
        return this;
    };
    /**
     * Set vector's length to 1 while keeping it's direction.
     *
     * @param n
     * @returns this
     */
    Vec2.prototype.normalize = function () {
        return this.divide(this.length);
    };
    /**
     * Inverse x and y coordinates.
     *
     * @returns this
     */
    Vec2.prototype.inverse = function () {
        return this.multiply(-1);
    };
    /**
     * Rotate vector around origin.
     *
     * @param angle in radians
     * @param origin (0, 0)
     * @param clockwise true
     */
    Vec2.prototype.rotate = function (angle, clockwise, origin) {
        if (clockwise === void 0) { clockwise = true; }
        if (origin === void 0) { origin = new Vec2(); }
        var length = this.length;
        var diff = this.clone().subtract(origin);
        if (!clockwise)
            angle *= -1;
        this.x = Math.cos(angle) * diff.x - Math.sin(angle) * diff.y;
        this.y = Math.sin(angle) * diff.x + Math.cos(angle) * diff.y;
        this.add(origin);
        this.setMagnitude(length);
        return this;
    };
    /**
     * Set explicit length for vector.
     *
     * @param mag
     * @returns this
     */
    Vec2.prototype.setMagnitude = function (mag) {
        return this.normalize().multiply(mag);
    };
    /**
     * Clamp length of vector between specified min and max.
     *
     * @param min
     * @param max
     * @returns this
     */
    Vec2.prototype.clampMagnitude = function (min, max) {
        if (this.length < min)
            this.setMagnitude(min);
        if (this.length > max)
            this.setMagnitude(max);
        return this;
    };
    /**
     * Calculate dot product.
     *
     * @param vec
     * @returns dot product of two vectors
     */
    Vec2.prototype.dot = function (vec) {
        return this.x * vec.x + this.y * vec.y;
    };
    /**
     * Calculate cross product.
     *
     * @param vec
     * @returns cross product of two vectors
     */
    Vec2.prototype.cross = function (vec) {
        return this.x * vec.y + this.y * vec.x;
    };
    /**
     * Check vector coordinates for equality.
     *
     * @param vec
     * @returns true if coordinates match
     */
    Vec2.prototype.equals = function (vec) {
        return this.x === vec.x && this.y === vec.y;
    };
    /**
     * Clones vector to a new instance.
     *
     * @returns new vector with same coordinates
     */
    Vec2.prototype.clone = function () {
        return new Vec2(this.x, this.y);
    };
    /**
     * Get distance to another vector.
     *
     * @param vec
     * @returns distance to vector
     */
    Vec2.prototype.distanceTo = function (vec) {
        return vec.clone().subtract(this).length;
    };
    /**
     * Get angle to another vector in range [-π, π].
     * fullRange shifts the range to [0, 2π].
     *
     * @param vec
     * @returns angle in radians
     */
    Vec2.prototype.angleTo = function (vec, fullRange) {
        if (fullRange === void 0) { fullRange = false; }
        var angle = Math.atan2(vec.y, vec.x) - Math.atan2(this.y, this.x);
        if (fullRange) {
            // [0, 2π]
            if (angle < 0)
                angle += 2 * Math.PI;
        }
        else {
            // [-π, π]
            if (angle > Math.PI) {
                angle -= 2 * Math.PI;
            }
            else if (angle <= -Math.PI) {
                angle += 2 * Math.PI;
            }
        }
        return angle;
    };
    /**
     * Create normalized up vector using the
     * web's coordinate system (-y up).
     *
     * @returns new vector
     */
    Vec2.up = function () { return new Vec2(0, -1); };
    /**
     * Create normalized down vector using the
     * web's coordinate system (+y down).
     *
     * @returns new vector
     */
    Vec2.down = function () { return new Vec2(0, 1); };
    /**
     * Create normalized vector pointing left.
     *
     * @returns new vector
     */
    Vec2.left = function () { return new Vec2(-1, 0); };
    /**
     * Create normalized vector pointing right.
     *
     * @returns new vector
     */
    Vec2.right = function () { return new Vec2(1, 0); };
    return Vec2;
}());

/**
 * Converts radians to degrees.
 *
 * @param radians
 * @returns degrees
 */
var toDegrees = function (radians) {
    return radians * 180 / Math.PI;
};
/**
 * Converts degrees to radians.
 *
 * @param degrees
 * @returns radians
 */
var toRadians = function (degrees) {
    return degrees * Math.PI / 180;
};
/**
 * Clamps a given value in between specified min and max.
 *
 * @param value
 * @param min
 * @param max
 * @returns clamped number
 */
var clamp = function (value, min, max) {
    return Math.min(Math.max(value, min), max);
};
/**
 * Performs a linear interpolation between two vectors.
 *
 * @param vecA
 * @param vecB
 * @param progress
 * @returns new vector
 */
var lerp = function (vecA, vecB, progress) {
    progress = clamp(progress, 0, 1);
    var dirVec = vecB.clone().subtract(vecA);
    return vecA.clone().add(dirVec.multiply(progress));
};

/**
 * Render a vector using specified canvas2d context.
 *
 * @param vec
 * @param ctx
 * @param options
 */
var draw = function (vec, ctx, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.origin, origin = _c === void 0 ? new Vec2() : _c, _d = _b.color, color = _d === void 0 ? 'black' : _d, _e = _b.width, width = _e === void 0 ? 3 : _e, _f = _b.arrow, arrow = _f === void 0 ? true : _f;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.translate.apply(ctx, origin.rawPosition);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo.apply(ctx, vec.rawPosition);
    ctx.stroke();
    if (arrow) {
        var arrowLength = clamp(2 * width, 7, 50);
        ctx.translate.apply(ctx, vec.rawPosition);
        ctx.rotate(Vec2.up().angleTo(vec));
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(arrowLength, arrowLength);
        ctx.moveTo(0, 0);
        ctx.lineTo(-arrowLength, arrowLength);
        ctx.stroke();
    }
    ctx.restore();
};
/**
 * Render multiple vectors with specified canvas2d context.
 *
 * @param vectors
 * @param ctx
 * @param options
 */
var drawMany = function (vectors, ctx, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.origin, origin = _c === void 0 ? new Vec2() : _c, _d = _b.color, color = _d === void 0 ? 'black' : _d, _e = _b.width, width = _e === void 0 ? 3 : _e, _f = _b.arrow, arrow = _f === void 0 ? true : _f;
    for (var _i = 0, vectors_1 = vectors; _i < vectors_1.length; _i++) {
        var vec = vectors_1[_i];
        draw(vec, ctx, { origin: origin, color: color, width: width, arrow: arrow });
    }
};

/**
 * Create a random vector in between two specified vectors.
 *
 * @param vecA
 * @param vecB
 * @returns new vector
 */
var randomBetween = function (vecA, vecB) {
    var diff = vecB.clone().subtract(vecA);
    diff.x *= Math.random();
    diff.y *= Math.random();
    return vecA.clone().add(diff);
};

exports.clamp = clamp;
exports.default = Vec2;
exports.draw = draw;
exports.drawMany = drawMany;
exports.lerp = lerp;
exports.randomBetween = randomBetween;
exports.toDegrees = toDegrees;
exports.toRadians = toRadians;
